import { format } from 'date-fns';
import { useState } from 'react';
import { LinkId } from '../../../../common/AnalyticsContext/LinkId';
import { useSearchFilters } from '../../../../common/components/SearchHeader/filters/useSearchFilters';
import {
  isSearchDateRange,
  SearchDate,
  SearchDatePreset,
} from '../../../../common/components/SearchHeader/types/FilterChoice';
import { DropDownEnumFilter } from '../../EnumFilter/DropDownEnumFilter/DropDownEnumFilter';
import { Option } from '../../EnumFilter/RadioGroup/RadioGroup';
import { DropDownFilterPill } from '../../FilterPill/DropDownFilterPill';
import { useOverlay } from '../../overlay/OverlayContext';
import { DateFilterRange } from './DateFilterRange';

const TITLE = 'Date';

const CustomType = 'Custom';
type CustomType = 'Custom';

const makeCaption = (
  date: SearchDate | undefined,
  availableOptions: Option<SearchDatePreset | CustomType | undefined>[],
): string => {
  if (!isSearchDateRange(date)) {
    return (date && availableOptions.find(it => it.value === date)?.label) || TITLE;
  }

  const startString = format(new Date(date.start), 'd LLL');
  const endString = date.end && format(new Date(date.end), ' - d LLL');
  return startString && `${startString}${endString || ''}`;
};

export const DateFilter = () => {
  const [filters, setFilters] = useSearchFilters();
  const { clear } = useOverlay();

  const { date } = filters.onlineAppointments ?? {};

  const [canSetDateRange, setCanSetDateRange] = useState(isSearchDateRange(date));

  const handleChange = (newDate: SearchDate | CustomType | undefined): void => {
    if (newDate === CustomType) {
      setCanSetDateRange(true);
    } else {
      setCanSetDateRange(isSearchDateRange(newDate));

      newDate !== date && setFilters({ ...filters, onlineAppointments: { date: newDate } });

      clear();
    }
  };

  const caption = makeCaption(date, OPTIONS);

  /** FIXME: a stopgap to hide the date-filter for telehealth searches while it is broken */
  if (filters.flag === 'telehealth' && !date) {
    return null;
  }

  return (
    <DropDownFilterPill
      hasValue={date !== undefined}
      onClear={() => setFilters({ ...filters, onlineAppointments: {} })}
      caption={caption}
      trackingId={LinkId.FILTER_BY_DATE_BUTTON}
      content={
        <>
          <DropDownEnumFilter<SearchDate | CustomType | undefined>
            value={canSetDateRange ? CustomType : date}
            filterTitle="Appointment date"
            options={OPTIONS}
            onChange={handleChange}
          />
          {canSetDateRange && (
            <DateFilterRange
              date={isSearchDateRange(date) ? date : undefined}
              onReset={() => handleChange(date)}
              onApply={handleChange}
            />
          )}
        </>
      }
    />
  );
};

const OPTIONS: Option<SearchDatePreset | CustomType | undefined>[] = [
  { label: 'Today', value: 'Today' },
  { label: 'Tomorrow', value: 'Tomorrow' },
  { label: 'Next 7 days', value: 'Next7Days' },
  { label: 'Next available appointment', value: undefined },
  {
    label: 'Choose a specific date or custom date ranges',
    value: CustomType,
  },
];
